// layouts/certificaciones/editar-certificacion.jsx
import { useEffect, useMemo, useState, useRef } from "react";

import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import FormPage from "ui/FormPage";
import { PrimaryButton, OutlineButton } from "ui/Buttons";
import FormTextField from "ui/FormTextField";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import {
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import {
  getCertificacion,
  updateCertificacion,
  updateCertStatus,
  notifyCertDecision,
  listFormatos,
  listPerformers,
  listGrupos,
  downloadCertPdf,
} from "./services/certificaciones";
import api, { buildApiUrl } from "services/api";
import { useAuth } from "layouts/auth/AuthContext";
import { getCountriesES } from "utils/countries";

/* ---------------- helpers ---------------- */

// === helpers de attachments ===
function attachmentUrl(name = "", tryWithoutExt = false) {
  if (!name) return null;

  const n = tryWithoutExt
    ? String(name).replace(/\.[^.]+$/, "")
    : name;

  return buildApiUrl(`/attachments/${encodeURIComponent(n)}`);
}

/** Imagen con fallback: primero intenta "<file>", si 404 prueba "<file-sin-ext>" */
function AttachmentImage({ name, alt, maxH = 240, style }) {
  const [src, setSrc] = useState(() => attachmentUrl(name, false));
  useEffect(() => { setSrc(attachmentUrl(name, false)); }, [name]);
  if (!name) return null;

  return (
    <Box sx={{ border: "1px solid #eee", p: 1, borderRadius: 1, display: "inline-block" }}>
      <img
        src={src}
        alt={alt || name}
        style={{ maxHeight: maxH, maxWidth: "100%", display: "block", ...(style || {}) }}
        onError={() => setSrc(attachmentUrl(name, true))}
      />
    </Box>
  );
}

function extractErrMessages(err) {
  const out = [];
  const d = err?.response?.data ?? err;
  const push = (m) => m && out.push(String(m));

  if (typeof d === "string") push(d);
  else if (Array.isArray(d)) d.forEach((it) => push(it?.message || JSON.stringify(it)));
  else if (d?.errors && Array.isArray(d.errors))
    d.errors.forEach((it) =>
      push(it?.message || `${it?.instancePath || it?.path || ""} ${it?.message || ""}`.trim())
    );
  else if (Array.isArray(d?.message)) {
    d.message.forEach((it) => {
      const field = String(
        it?.instancePath || it?.path || ""
      ).replace(/^\//, "");

      const msg =
        it?.message ||
        "Valor inválido";

      push(
        field
          ? `${field}: ${msg}`
          : msg
      );
    });
  } else if (d?.message) {
    push(d.message);
  }
  else push(err?.message || "Error desconocido");

  return out;
}

const statusChip = (s) => {
  const v = Number(s);
  if (v === 1) return <Chip size="small" color="success" label="Aceptado" />;
  if (v === 2) return <Chip size="small" color="error" label="Rechazado" />;
  return <Chip size="small" color="warning" label="Pendiente" />;
};

// 👉 NUEVO: sin usar Date, solo recorta YYYY-MM-DD (evita desfase por zona horaria)
const toYMD = (v) => {
  if (!v) return "";
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
};

const guessMimeFromName = (name = "") => {
  if (/\.pdf$/i.test(name)) return "application/pdf";
  if (/\.png$/i.test(name)) return "image/png";
  if (/\.jpe?g$/i.test(name)) return "image/jpeg";
  if (/\.gif$/i.test(name)) return "image/gif";
  if (/\.webp$/i.test(name)) return "image/webp";
  return "";
};

const isPdf = (p) => !!p && (p.type?.includes("pdf") || /\.pdf$/i.test(p.src || ""));
const isImg = (p) =>
  !!p && (p.type?.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(p.src || ""));

/* Obtiene un blob o usa URL directa si viene del backend */
async function resolvePreviewURL(record, field) {
  const filename = record?.[field];
  if (!filename) return null;

  // 1) URL directa en el payload (label_url / report_url / cover_url)
  const directUrl = record?.[`${field}_url`] || record?.[`${field}Url`] || null;
  if (directUrl) {
    return { url: directUrl, type: guessMimeFromName(directUrl), src: directUrl };
  }

  // 2) /attachments con NOMBRE ORIGINAL (sin agregar .png)
  const noExt = String(filename).replace(/\.[^.]+$/, "");

  const candidates = [
    `/attachments/${encodeURIComponent(filename)}`,
    `/attachments/${encodeURIComponent(noExt)}`,
    `/certified/${encodeURIComponent(record.id)}/file/${encodeURIComponent(field)}`,
    `/certified/${encodeURIComponent(record.id)}/file?field=${encodeURIComponent(field)}`,
    `/files/${encodeURIComponent(filename)}`,
    `/uploads/${encodeURIComponent(filename)}`,
    `/storage/${encodeURIComponent(filename)}`,
  ];

  for (const url of candidates) {
    try {
      const res = await api.get(url, { responseType: "blob" }); // token via axios
      if (res?.status === 200 && res?.data) {
        const blob = res.data;
        const type = blob.type || guessMimeFromName(filename);
        const objectUrl = URL.createObjectURL(blob);
        return { url: objectUrl, type, src: url, _blob: true };
      }
    } catch {
      // probar siguiente
    }
  }
  return null;
}

/* ===== Helpers para autocomplete remoto / local (igual que en nueva-certificación) ===== */

const unwrapList = (data) =>
  Array.isArray(data) ? data
  : Array.isArray(data?.data) ? data.data
  : Array.isArray(data?.page) ? data.page
  : Array.isArray(data?.results) ? data.results
  : [];

function debounceFn(fn, ms = 300) { 
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

async function fetchPagedOptions({ endpoint, q = "", page = 0, pageSize = 20, nameKey = "name" }) {
  const params = { __limit: pageSize, __offset: page * pageSize };
  if (q.trim()) params[`${nameKey}[$contains]`] = q.trim();
  const { data } = await api.get(endpoint, { params });
  const list = unwrapList(data);
  return (list || [])
    .map((x) => ({
      id: String(x.id ?? x._id ?? ""),
      name: x[nameKey] ?? x.nombre ?? x.title ?? `#${x.id ?? x._id ?? ""}`,
    }))
    .filter((o) => o.id);
}

// trae TODOS los sellos paginando /recordLabel
async function fetchAllRecordLabels() {
  const pageSize = 1000;
  let all = [];
  let offset = 0;

  for (;;) {
    const { data } = await api.get("/recordLabel", {
      params: { __limit: pageSize, __offset: offset },
    });
    const page = unwrapList(data);
    if (!page.length) break;
    all = all.concat(page);
    if (page.length < pageSize) break;
    offset += page.length;
  }
  return all;
}

/* Select con label clickeable + Autocomplete (single) */
function ClickableAutoSelect({
  id,
  label,
  value,
  onChange,
  placeholder = "",
  endpoint = null,      // si es null => modo local (solo initialOptions)
  initialOptions = [],  // [{id,name}]
  nameKey = "name",
  disabled = false,
  required = false,
  fullWidth = true,
  size = "medium",
}) {
  const labelId = `${id}-label`;

  // ⬅️ PRIMERO calculamos localMode
  const localMode = !endpoint;

  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);

  // ref al trigger del Select
  const inputRef = useRef(null);

  // === 1) cuando cambian los catálogos iniciales (modo local) ===
  useEffect(() => {
    if (!localMode) return;
    const mapped = (initialOptions || []).map((o) => ({
      id: String(o.id),
      name: o[nameKey] ?? o.name ?? o.nombre ?? o.title ?? String(o.id),
    }));
    setOpts(mapped);
  }, [initialOptions, nameKey, localMode]);

  // === 2) si endpoint está definido, podemos cargar opciones remotas on mount ===
  useEffect(() => {
    if (!endpoint || localMode) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(endpoint, { params: { __limit: 1000, __offset: 0 } });
        if (cancel) return;
        const list = unwrapList(data);
        const mapped = list.map((o) => ({
          id: String(o.id ?? o._id),
          name: o[nameKey] ?? o.name ?? o.nombre ?? o.title ?? String(o.id ?? o._id),
        }));
        setOpts(mapped);
      } catch {
        if (!cancel) setOpts([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [endpoint, nameKey, localMode]);

  const openFromLabel = () => {
    if (!inputRef.current || disabled) return;
    const el = inputRef.current;
    el.focus();
    if (typeof el.click === "function") el.click();
    else if (el.parentElement && typeof el.parentElement.click === "function") {
      el.parentElement.click();
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      required={required}
      disabled={disabled}
      size={size}
    >
      <InputLabel
        id={labelId}
        sx={{ pointerEvents: "auto", cursor: disabled ? "default" : "pointer" }}
        onClick={openFromLabel}
        onMouseDown={(e) => e.preventDefault()}
      >
        {label}
      </InputLabel>

      <Select
        labelId={labelId}
        id={id}
        label={label}
        value={String(value ?? "")}
        onChange={handleChange}
        displayEmpty
        inputRef={inputRef}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                {placeholder || "Selecciona…"}
              </Typography>
            );
          }
          const opt = opts.find((o) => String(o.id) === String(selected));
          return opt?.name || selected;
        }}
        MenuProps={{
          PaperProps: { style: { maxHeight: 360 } },
        }}
      >
        {!required && (
          <MenuItem value="">
            <em>Sin seleccionar</em>
          </MenuItem>
        )}
        {opts.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        ))}
      </Select>

      {loading && (
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Cargando…
          </Typography>
        </Box>
      )}
    </FormControl>
  );
}

/* ================= component ================= */
const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const MAX_TOTAL_UPLOAD_MB = 18;
const MAX_TOTAL_UPLOAD_BYTES =
  MAX_TOTAL_UPLOAD_MB * 1024 * 1024;

export default function EditarCertificacion() {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
    user,
    baseUser,
    isImpersonating,
  } = useAuth();

  const isAdmin =
    baseUser?.is_admin === true ||
    baseUser?.is_admin === 1 ||
    String(baseUser?.is_admin) === "1";

  const actingAsAdmin = isAdmin && !isImpersonating;
  const assignedGroupIds = useMemo(() => {
    if (Array.isArray(user?.groups) && user.groups.length) {
      return [
        ...new Set(
          user.groups
            .map((g) =>
              typeof g === "object" && g !== null
                ? g.id ?? g._id
                : g
            )
            .filter(
              (id) =>
                id !== null &&
                id !== undefined &&
                id !== ""
            )
            .map(String)
        ),
      ];
    }

    // compatibilidad con usuarios antiguos
    if (
      user?.group_id !== null &&
      user?.group_id !== undefined &&
      user?.group_id !== ""
    ) {
      return [String(user.group_id)];
    }

    return [];
  }, [user?.groups, user?.group_id]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  // visor fullscreen (img o pdf)
  const [viewer, setViewer] = useState({ open: false, src: "", type: "", title: "" });
  const openViewer = (preview, title) => {
    if (!preview?.url) return;
    setViewer({ open: true, src: preview.url, type: preview.type || "", title: title || "" });
  };
  const closeViewer = () => setViewer({ open: false, src: "", type: "", title: "" });

  // campos
  const [artist, setArtist] = useState("");
  const [title, setTitle] = useState("");
  const [album, setAlbum] = useState("");
  const [isrc, setIsrc] = useState("");
  const [award, setAward] = useState("");
  const [formatId, setFormatId] = useState("");
  const [performerId, setPerformerId] = useState("");
  const [recordLabelId, setRecordLabelId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [saleUnits, setSaleUnits] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [postAt, setPostAt] = useState("");
  const [nationality, setNationality] = useState("");
  const [isPublic, setIsPublic] = useState(true);

  // estatus
  const [status, setStatus] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [savedStatus, setSavedStatus] = useState(0);
  const [savedRejectReason, setSavedRejectReason] = useState("");


  // descarga PDF (certificado)
  const [downloading, setDownloading] = useState(false);
  const canDownload = Number(status) === 1;

  // catálogos
  const [optsFormatos, setOptsFormatos] = useState([]);
  const [optsPerformers, setOptsPerformers] = useState([]);
  const [optsSellos, setOptsSellos] = useState([]);
  const [optsGrupos, setOptsGrupos] = useState([]);
  const [optsCountries, setOptsCountries] = useState([]);


  const effectiveGroupId = String(groupId || "");

  const filteredSellos = useMemo(() => {
    if (!effectiveGroupId) {
      return [];
    }

    return (optsSellos || []).filter((label) => {
      const labelGroupIds =
        Array.isArray(label?.groups) &&
        label.groups.length
          ? label.groups
              .map((g) =>
                typeof g === "object" && g !== null
                  ? g.id ?? g._id
                  : g
              )
              .filter(
                (id) =>
                  id !== null &&
                  id !== undefined &&
                  id !== ""
              )
              .map(String)
          : label?.group_id != null
          ? [String(label.group_id)]
          : [];

      return labelGroupIds.includes(
        String(effectiveGroupId)
      );
    });
  }, [optsSellos, effectiveGroupId]);

  // archivos actuales (nombres)
  const [labelName, setLabelName] = useState("");
  const [reportName, setReportName] = useState("");
  const [coverName, setCoverName] = useState("");

  // previews actuales (o de blob remoto)
  const [labelPreview, setLabelPreview] = useState(null);
  const [reportPreview, setReportPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  // reemplazos nuevos (archivos y previews locales)
  const [newLabelFile, setNewLabelFile] = useState(null);
  const [newReportFile, setNewReportFile] = useState(null);
  const [newCoverFile, setNewCoverFile] = useState(null);

  const [newLabelURL, setNewLabelURL] = useState("");
  const [newReportURL, setNewReportURL] = useState("");
  const [newCoverURL, setNewCoverURL] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setErrors([]);
      try {
        const [r, f, p, l, g] = await Promise.all([
          getCertificacion(id),
          listFormatos(),
          listPerformers(),
          fetchAllRecordLabels(),
          listGrupos({
            __limit: 1000,
            __offset: 0,
            __sort: { name: 1 },
          }),
        ]);
        if (cancel) return;

        let st = r?.status;
        if (typeof st === "boolean") st = st ? 1 : 0;
        if (![0, 1, 2].includes(Number(st))) st = 0;
        setStatus(Number(st));
        setRejectReason(r?.reject_reason ?? r?.reason ?? "");
        setSavedStatus(Number(st));
        setSavedRejectReason(r?.reject_reason ?? r?.reason ?? "");
        setArtist(r?.artist ?? "");
        setTitle(r?.title ?? "");
        setAlbum(r?.album ?? "");
        setIsrc(r?.isrc ?? "");
        setAward(r?.award ?? "");
        setFormatId(r?.format_id ?? "");
        setPerformerId(r?.performer_id ?? "");
        setRecordLabelId(r?.record_label_id ?? "");
        setGroupId(r?.group_id ?? "");
        setSaleUnits(r?.sale_units ?? "");
        setReleaseDate(toYMD(r?.release_date ?? r?.releaseDate ?? ""));
        setPostAt(toYMD(r?.post_at ?? r?.postAt ?? ""));       // 👈 carga post_at
        setNationality(r?.nationality ?? "");
        setIsPublic(!!r?.is_public);

        setLabelName(r?.label || "");
        setReportName(r?.report || "");
        setCoverName(r?.cover || "");

        setOptsFormatos(f || []);
        setOptsPerformers(p || []);
        setOptsSellos(l || []);
        setOptsGrupos(g || []);
        setOptsCountries(getCountriesES() || []);

        const [lp, rp, cp] = await Promise.all([
          resolvePreviewURL(r, "label"),
          resolvePreviewURL(r, "report"),
          resolvePreviewURL(r, "cover"),
        ]);
        if (!cancel) {
          setLabelPreview(lp);
          setReportPreview(rp);
          setCoverPreview(cp);
        }
      } catch (e) {
        if (!cancel) setErrors(extractErrMessages(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  // revoke objectURLs locales al desmontar/cambiar
  useEffect(() => () => { try { if (newLabelURL) URL.revokeObjectURL(newLabelURL); } catch {} }, [newLabelURL]);
  useEffect(() => () => { try { if (newReportURL) URL.revokeObjectURL(newReportURL); } catch {} }, [newReportURL]);
  useEffect(() => () => { try { if (newCoverURL) URL.revokeObjectURL(newCoverURL); } catch {} }, [newCoverURL]);
  // revoke blobs remotos
  useEffect(() => () => { try { if (labelPreview?._blob)  URL.revokeObjectURL(labelPreview.url); } catch {} }, [labelPreview]);
  useEffect(() => () => { try { if (reportPreview?._blob) URL.revokeObjectURL(reportPreview.url); } catch {} }, [reportPreview]);
  useEffect(() => () => { try { if (coverPreview?._blob)  URL.revokeObjectURL(coverPreview.url); } catch {} }, [coverPreview]);

  const isValid = useMemo(
    () =>
      !!artist.trim() &&
      !!title.trim() &&
      !!isrc.trim() &&
      !!effectiveGroupId &&
      !!recordLabelId,
    [
      artist,
      title,
      isrc,
      effectiveGroupId,
      recordLabelId,
    ]
  );

  /* ---------- handlers archivos (reemplazo) ---------- */
  const handlePickFile = (kind) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
      setErrors([`El archivo seleccionado supera el límite de ${MAX_FILE_MB} MB.`]);
      return;
    }

    const url = URL.createObjectURL(file);
    if (kind === "label") {
      setNewLabelFile(file);
      setNewLabelURL(url);
      setLabelPreview({ url, type: file.type || "application/pdf", src: file.name, _local: true });
      setLabelName(file.name);
    } else if (kind === "report") {
      setNewReportFile(file);
      setNewReportURL(url);
      setReportPreview({ url, type: file.type || "application/pdf", src: file.name, _local: true });
      setReportName(file.name);
    } else if (kind === "cover") {
      setNewCoverFile(file);
      setNewCoverURL(url);
      setCoverPreview({ url, type: file.type || "image/*", src: file.name, _local: true });
      setCoverName(file.name);
    }
  };

  /* ---------- guardar generales (con o sin archivos) ---------- */
  const onSubmit = async (e) => {
  e.preventDefault();

  if (!isValid) return;

  if (!isrc.trim()) {
    setErrors(["El ISRC es obligatorio."]);
    return;
  }

  if (!effectiveGroupId) {
    setErrors(["El grupo es obligatorio."]);
    return;
  }

  if (!recordLabelId) {
    setErrors(["El sello es obligatorio."]);
    return;
  }

  setSaving(true);
  setErrors([]);

  try {
    const fields = {
      artist: artist.trim(),
      title: title.trim(),
      album: album.trim() || undefined,
      isrc: isrc.trim(),
      award: award.trim() || undefined,
      sale_units: saleUnits ? Number(saleUnits) : undefined,
      format_id: formatId ? Number(formatId) : undefined,
      performer_id: performerId ? Number(performerId) : undefined,
      record_label_id: recordLabelId
        ? Number(recordLabelId)
        : undefined,
      group_id: effectiveGroupId
        ? Number(effectiveGroupId)
        : undefined,
      release_date: toYMD(releaseDate) || undefined,
      post_at: toYMD(postAt) || undefined,
      nationality: nationality || undefined,
      is_public: !!isPublic,
    };

    Object.keys(fields).forEach((k) => {
      if (
        fields[k] == null ||
        fields[k] === ""
      ) {
        delete fields[k];
      }
    });

    // 1. Actualiza primero los datos tipados como JSON
    await updateCertificacion(id, fields);

    // 2. Si hay archivos nuevos, súbelos aparte
    const anyFile =
      newLabelFile ||
      newReportFile ||
      newCoverFile;

    if (anyFile) {
      const fd = new FormData();

      if (newLabelFile) {
        fd.append("label", newLabelFile);
      }

      if (newReportFile) {
        fd.append("report", newReportFile);
      }

      if (newCoverFile) {
        fd.append("cover", newCoverFile);
      }

      await updateCertificacion(id, fd);
    }

    navigate("/certificaciones", {
      replace: true,
      state: { refresh: Date.now() },
    });
  } catch (e) {
    const msgs = extractErrMessages(e);

    if (msgs.length === 0) {
      msgs.push(
        "No se pudo actualizar la certificación."
      );
    }

    setErrors(msgs);
  } finally {
    setSaving(false);
  }
};

  /* ---------- guardar estatus (solo admin) ---------- */
  const handleStatusChange = async () => {
    setSaving(true);
    setErrors([]);
    try {
      let reason = undefined;
  
      if (Number(status) === 2) {
        if (!rejectReason.trim()) {
          setSaving(false);
          setErrors(["Escribe el motivo del rechazo."]);
          return;
        }
        reason = rejectReason.trim();
      }
  
      const payload = {
        status: Number(status),
        ...(reason ? { reject_reason: reason } : {}),
      };
  
      await updateCertificacion(id, payload);
      await notifyCertDecision(id, { status: Number(status), reason });
  
      // ✅ snapshot para saber que "estatus ya quedó guardado"
      setSavedStatus(Number(status));
      setSavedRejectReason(reason || "");
  
      // ✅ opcional: feedback sin salir
      // setErrors([]);  // ya está
    } catch (e) {
      setErrors(extractErrMessages(e));
    } finally {
      setSaving(false);
      setRejectOpen(false);
    }
  };  

  const onAdminSelectStatus = (value) => {
    const v = Number(value);
    setStatus(v);
    // Si selecciona "Rechazar", mostramos el diálogo,
    // pero YA NO borramos el motivo previo
    if (v === 2) {
      setRejectOpen(true);
    } else {
      setRejectOpen(false);
    }
  };


  /* ---------- descarga certificado (status = 1) ---------- */
  const onDownload = async () => {
    try {
      setDownloading(true);
      const finalName =
        `certificado_${(artist || "cert").replace(/\s+/g, "_")}_${(title || "").replace(/\s+/g, "_")}.pdf`;

      await downloadCertPdf(id, finalName);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) {
        alert("La certificación aún no está aceptada (403).");
      } else if (code === 404) {
        alert("No se encontró el PDF (404). Verifica el ID y el endpoint /certified/:id/certificate.pdf.");
      } else {
        alert(e?.response?.data?.message || e.message || "Error al descargar");
      }
    } finally {
      setDownloading(false);
    }
  };

  const statusDirty =
  Number(status) !== Number(savedStatus) ||
  (Number(status) === 2 && (rejectReason || "").trim() !== (savedRejectReason || "").trim());


  return (
    <FormPage
      title="Editar certificación"
      onBack={() => navigate("/certificaciones")}
      pageMaxWidth={1200}
      contentMaxWidth={1000}
      actions={
        <>
          <PrimaryButton type="submit" form="form-cert" disabled={!isValid || saving || loading}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </PrimaryButton>
          <OutlineButton onClick={() => navigate("/certificaciones")}>Cancelar</OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((m, i) => (
            <div key={i}>{m}</div>
          ))}
        </Alert>
      )}

      {/* Estatus actual + descarga */}
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle1">Estatus actual:</Typography>
            {statusChip(status)}
          </Stack>

          <Button
            color="dark" variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={onDownload}
            disabled={!canDownload || downloading}
          >
            {downloading ? "Generando..." : "Descargar certificado"}
          </Button>
        </Stack>
      </Card>

      {/* Formulario principal */}
      <form id="form-cert" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Artista"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              required
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormTextField label="Álbum" value={album} onChange={(e) => setAlbum(e.target.value)} disabled={loading} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormTextField
              label="ISRC / Código"
              value={isrc}
              onChange={(e) => setIsrc(e.target.value)}
              required
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormTextField
              label="Premio / Award"
              value={award}
              onChange={(e) => setAward(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Unidades"
              type="number"
              value={saleUnits}
              onChange={(e) => setSaleUnits(e.target.value)}
              disabled={loading}
            />
          </Grid>

          {/* Formato */}
          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="format"
              label="Formato"
              value={String(formatId || "")}
              onChange={(id) => setFormatId(id)}
              endpoint={null}
              nameKey="name"
              placeholder="Selecciona formato"
              initialOptions={optsFormatos}
              disabled={loading}
            />
          </Grid>

          {/* Intérprete */}
          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="performer"
              label="Intérprete"
              value={String(performerId || "")}
              onChange={(id) => setPerformerId(id)}
              endpoint={null}
              nameKey="name"
              placeholder="Selecciona intérprete"
              initialOptions={optsPerformers}
              disabled={loading}
            />
          </Grid>

          {/* Sello */}
          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="label"
              label="Sello*"
              value={String(recordLabelId || "")}
              onChange={(id) => setRecordLabelId(id)}
              endpoint={null}
              nameKey="name"
              placeholder={
                effectiveGroupId
                  ? "Selecciona sello"
                  : "Selecciona primero un grupo"
              }
              initialOptions={filteredSellos}
              disabled={loading || !effectiveGroupId}
              required
            />
          </Grid>

          {/* Grupo */}
          <Grid item xs={12} md={3}>
            {actingAsAdmin ? (
              <ClickableAutoSelect
                id="group"
                label="Grupo*"
                value={String(groupId || "")}
                onChange={(id) => {
                  setGroupId(id);
                  setRecordLabelId("");
                }}
                endpoint={null}
                nameKey="name"
                placeholder="Selecciona grupo"
                initialOptions={optsGrupos}
                disabled={loading}
                required
              />
            ) : assignedGroupIds.length > 1 ? (
              <ClickableAutoSelect
                id="group"
                label="Grupo*"
                value={String(groupId || "")}
                onChange={(id) => {
                  setGroupId(id);
                  setRecordLabelId("");
                }}
                endpoint={null}
                nameKey="name"
                placeholder="Selecciona grupo"
                initialOptions={optsGrupos.filter((g) =>
                  assignedGroupIds.includes(
                    String(g.id ?? g._id)
                  )
                )}
                disabled={loading}
                required
              />
            ) : (
              <ClickableAutoSelect
                id="group"
                label="Grupo*"
                value={String(groupId || "")}
                onChange={() => {}}
                endpoint={null}
                nameKey="name"
                initialOptions={optsGrupos.filter((g) =>
                  assignedGroupIds.includes(
                    String(g.id ?? g._id)
                  )
                )}
                disabled
                required
              />
            )}
          </Grid>

          {/* Nacionalidad */}
          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="nationality"
              label="Nacionalidad (código ISO)"
              value={nationality}
              onChange={(code) => setNationality((code || "").toUpperCase())}
              endpoint={null}
              placeholder="Selecciona nacionalidad"
              initialOptions={optsCountries.map(c => ({
                id: c.code,
                name: `${c.name} (${c.code})`,
              }))}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormTextField
              label="Fecha de lanzamiento"
              type="date"
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* NUEVO: Fecha de publicación */}
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Fecha de publicación"
              type="date"
              value={postAt}
              onChange={(e) => setPostAt(e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={<Switch checked={!!isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
              label={`Público: ${isPublic ? "Sí" : "No"}`}
            />
          </Grid>
        </Grid>
      </form>

      {/* Archivos adjuntos / Preview + Reemplazo */}
      {/* ... resto del componente igual que antes ... */}
      {/* (Todo el bloque de archivos, revisión y diálogos permanece sin cambios) */}
      {/* --- Archivos adjuntos --- */}
      <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Archivos adjuntos
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Tamaño máximo por archivo: {MAX_FILE_MB} MB
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3} alignItems="stretch">

        {/* =========================
            ARCHIVO SELLO
        ========================== */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle2">
              Archivo sello
            </Typography>

            {/* PREVIEW */}
            <Box
              sx={{
                mt: 1,
                height: 240,
                width: "100%",
                border: "1px solid #eee",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {labelPreview ? (
                isPdf(labelPreview) ? (
                  <object
                    data={labelPreview.url}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{
                      display: "block",
                      border: "none",
                    }}
                  >
                    <a
                      href={labelPreview.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir PDF
                    </a>
                  </object>
                ) : isImg(labelPreview) ? (
                  <img
                    src={labelPreview.url}
                    alt={labelName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 1, textAlign: "center" }}
                  >
                    Vista previa no disponible
                  </Typography>
                )
              ) : labelName ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <AttachmentImage
                    name={labelName}
                    alt={labelName}
                  />
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Sin archivo o no disponible
                </Typography>
              )}
            </Box>

            {/* ACCIONES */}
            <Box
              sx={{
                mt: 1.5,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Button
                color="dark"
                variant="contained"
                size="small"
                component="label"
                fullWidth
                sx={{
                  minHeight: 40,
                  whiteSpace: "nowrap",
                }}
              >
                Reemplazar

                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handlePickFile("label")}
                />
              </Button>

              {labelPreview?.url && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 0.75,
                    width: "100%",
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={() =>
                      openViewer(labelPreview, labelName)
                    }
                    sx={{
                      minHeight: 36,
                      whiteSpace: "nowrap",
                      color: "#000",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#0d47a1",
                        backgroundColor:
                          "rgba(25,118,210,0.08)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Ver
                  </Button>

                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    href={labelPreview.url}
                    download={labelName}
                    sx={{
                      minHeight: 36,
                      whiteSpace: "nowrap",
                      color: "#000",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#0d47a1",
                        backgroundColor:
                          "rgba(25,118,210,0.08)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Descargar
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {/* =========================
            ARCHIVO REPORTE
        ========================== */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle2">
              Archivo reporte
            </Typography>

            {/* PREVIEW */}
            <Box
              sx={{
                mt: 1,
                height: 240,
                width: "100%",
                border: "1px solid #eee",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {reportPreview ? (
                isPdf(reportPreview) ? (
                  <object
                    data={reportPreview.url}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{
                      display: "block",
                      border: "none",
                    }}
                  >
                    <a
                      href={reportPreview.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir PDF
                    </a>
                  </object>
                ) : isImg(reportPreview) ? (
                  <img
                    src={reportPreview.url}
                    alt={reportName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 1, textAlign: "center" }}
                  >
                    Vista previa no disponible
                  </Typography>
                )
              ) : reportName ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <AttachmentImage
                    name={reportName}
                    alt={reportName}
                  />
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Sin archivo o no disponible
                </Typography>
              )}
            </Box>

            {/* ACCIONES */}
            <Box
              sx={{
                mt: 1.5,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Button
                color="dark"
                variant="contained"
                size="small"
                component="label"
                fullWidth
                sx={{
                  minHeight: 40,
                  whiteSpace: "nowrap",
                }}
              >
                Reemplazar

                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handlePickFile("report")}
                />
              </Button>

              {reportPreview?.url && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 0.75,
                    width: "100%",
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={() =>
                      openViewer(reportPreview, reportName)
                    }
                    sx={{
                      minHeight: 36,
                      whiteSpace: "nowrap",
                      color: "#000",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#0d47a1",
                        backgroundColor:
                          "rgba(25,118,210,0.08)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Ver
                  </Button>

                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    href={reportPreview.url}
                    download={reportName}
                    sx={{
                      minHeight: 36,
                      whiteSpace: "nowrap",
                      color: "#000",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#0d47a1",
                        backgroundColor:
                          "rgba(25,118,210,0.08)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Descargar
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {/* =========================
            PORTADA
        ========================== */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle2">
              Portada
            </Typography>

            {/* PREVIEW */}
            <Box
              sx={{
                mt: 1,
                height: 240,
                width: "100%",
                border: "1px solid #eee",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {coverPreview?.url ? (
                <img
                  src={coverPreview.url}
                  alt={coverName}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              ) : coverName ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <AttachmentImage
                    name={coverName}
                    alt={coverName}
                  />
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Sin archivo o no disponible
                </Typography>
              )}
            </Box>

            {/* ACCIONES */}
            <Box
              sx={{
                mt: 1.5,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Button
                color="dark"
                variant="contained"
                size="small"
                component="label"
                fullWidth
                sx={{
                  minHeight: 40,
                  whiteSpace: "nowrap",
                }}
              >
                Reemplazar

                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handlePickFile("cover")}
                />
              </Button>

              {coverPreview?.url && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 0.75,
                    width: "100%",
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={() =>
                      openViewer(coverPreview, coverName)
                    }
                    sx={{
                      minHeight: 36,
                      whiteSpace: "nowrap",
                      color: "#000",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#0d47a1",
                        backgroundColor:
                          "rgba(25,118,210,0.08)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Ver
                  </Button>

                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    href={coverPreview.url}
                    download={coverName}
                    sx={{
                      minHeight: 36,
                      whiteSpace: "nowrap",
                      color: "#000",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#0d47a1",
                        backgroundColor:
                          "rgba(25,118,210,0.08)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Descargar
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

      </Grid>
      </Card>

      {/* Revisión (solo admin) */}
      {actingAsAdmin && (
        <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Revisión
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <RadioGroup
            row
            value={String(status)}
            onChange={(e) => onAdminSelectStatus(e.target.value)}
          >
            <FormControlLabel value="0" control={<Radio />} label="Pendiente" />
            <FormControlLabel value="1" control={<Radio />} label="Aceptar" />
            <FormControlLabel value="2" control={<Radio />} label="Rechazar" />
          </RadioGroup>

          {/* 👇 Motivo editable cuando está en estado Rechazado */}
          {Number(status) === 2 && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Motivo del rechazo"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                placeholder="Describe por qué se rechaza esta certificación"
              />
            </Box>
          )}

          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button
              color="dark"
              variant="contained"
              onClick={handleStatusChange}
              disabled={saving || !statusDirty}
            >
              Guardar estatus
            </Button>
            <Typography variant="caption" color="text.secondary">
              Se notificará al usuario creador al guardar.
            </Typography>
          </Stack>
        </Card>
      )}


      {/* Diálogo motivo rechazo */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Motivo del rechazo</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            label="Describe el motivo"
            multiline
            minRows={3}
            fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleStatusChange}>
            Guardar y notificar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visor fullscreen para PDFs / imágenes */}
      <Dialog fullScreen open={viewer.open} onClose={closeViewer}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={closeViewer} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {viewer.title || "Vista previa"}
            </Typography>
            {viewer.src && (
              <Button color="inherit" href={viewer.src} download>
                Descargar
              </Button>
            )}
          </Toolbar>
        </AppBar>

        {String(viewer.type || "").includes("pdf") ? (
          <iframe title={viewer.title || "PDF"} src={viewer.src} style={{ border: 0, width: "100%", height: "100%" }} />
        ) : (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <img src={viewer.src} alt={viewer.title} style={{ maxWidth: "100%", height: "auto" }} />
          </Box>
        )}
      </Dialog>
    </FormPage>
  );
}
